import React, { useState, useCallback, useMemo } from 'react';
import YearlyHeader from './YearlyHeader';
import useSelectedSensor from '../../hooks/useSelectedSensor';
import useSensorOptions from '../../hooks/useSensorOptions';
import useCompanyOptions from '../../hooks/useCompanyOptions';
import useSelectedCompany from '../../hooks/useSelectedCompany';
import YearlyContent from './YearlyContent';
import { Box, Grid, Paper, useTheme, useMediaQuery } from '@mui/material';
import { useYearlyDataQuery, useAdultnChildYearlyDataQuery, useGenderYearlyDataQuery, useGetAddonByDeviceQuery } from '../../services/api';
import YearlyFilterBox from './YearlyFilterBox';
import YearlyLabel from './YearlyLabel';
import { useSelector } from 'react-redux';
import useGroupOptions from '../../hooks/useGroupOptions';
import useSelectedGroup from '../../hooks/useSelectedGroup';

const Yearly = () => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { company, role } = useSelector((state) => state.global);
    const isSuperOrPowerAdmin = role === 'Super Admin' || role === 'Power Admin';

    // Company, Group and Device option
    const { companyOptions } = useCompanyOptions();
    const { selectedCompany, selectedCompanyId, handleCompanyChange } = useSelectedCompany({ companyOptions });
    const groupOptions = useGroupOptions(isSuperOrPowerAdmin ? selectedCompanyId : company);
    const sensorOptions = useSensorOptions(isSuperOrPowerAdmin ? selectedCompanyId : company);
    const { selectedGroup, handleGroupChange } = useSelectedGroup(groupOptions, isSuperOrPowerAdmin ? selectedCompanyId : company);
    const filteredSensorOptions = useMemo(() => {
        if (!groupOptions.length) {
            return sensorOptions;
        }

        const selectedGroupObj = groupOptions.find((group) => group._id === selectedGroup);
        const selectedDeviceIds = selectedGroupObj?.deviceIds || [];

        return sensorOptions.filter((sensor) => selectedDeviceIds.includes(sensor._id));
    }, [sensorOptions, groupOptions, selectedGroup]);
    const { selectedSensor, handleSensorChange, getSelectedSensorLabel } = useSelectedSensor(filteredSensorOptions, isSuperOrPowerAdmin ? selectedCompany : company);

    const [showLabels, setShowLabels] = useState(true);
    const [tabValue, setTabValue] = useState(0);

    // API
    const { data: addonData } = useGetAddonByDeviceQuery(selectedSensor, { skip: !selectedSensor });
    const { data: yearlyData, isLoading: yearlyLoading, error: yearlyError } = useYearlyDataQuery({ mac: selectedSensor }, { skip: tabValue !== 0 || !selectedSensor });
    const { data: adultChildData, isLoading: adultChildLoading, error: adultChildError } = useAdultnChildYearlyDataQuery({ mac: selectedSensor }, { skip: tabValue !== 1 || !selectedSensor });
    const { data: genderData, isLoading: genderLoading, error: genderError } = useGenderYearlyDataQuery({ mac: selectedSensor }, { skip: tabValue !== 2 || !selectedSensor });

    const processedData = tabValue === 0 ? yearlyData : tabValue === 1 ? adultChildData : tabValue === 2 ? genderData : null;
    const chartIsLoading = tabValue === 0 ? yearlyLoading : tabValue === 1 ? adultChildLoading : tabValue === 2 ? genderLoading : null;
    const error = tabValue === 0 ? yearlyError : tabValue === 1 ? adultChildError : tabValue === 2 ? genderError : null;

    const handleShowLabelsChange = useCallback((event) => {
        setShowLabels(event.target.checked);
    }, []);

    // Handle tab change
    const handleTabChange = (newValue) => {
        setTabValue(newValue);
    };

    return (
        <Box m={isSmallScreen ? '0.5rem' : '1rem 2.5rem'}>
            {isSmallScreen ? (
                //Mobile view
                <Grid container spacing={2} paddingBottom={3}>
                    <Grid item xs={12}>
                        <YearlyHeader isSmallScreen={isSmallScreen} />
                        <YearlyLabel sensorLabel={getSelectedSensorLabel()} isSmallScreen={isSmallScreen} tabValue={tabValue} onTabChange={handleTabChange} addonData={addonData} />
                    </Grid>
                    <Grid item xs={12}>
                        <Paper
                            sx={{
                                position: 'sticky',
                                top: 20,
                                height: 'auto',
                                overflow: 'auto',
                                borderRadius: '15px',
                                boxShadow: '8px 8px 20px rgba(0, 0, 0, 0.1)',
                            }}
                        >
                            <YearlyFilterBox
                                sensorLabel={getSelectedSensorLabel()}
                                isSmallScreen={isSmallScreen}
                                showLabels={showLabels}
                                onShowLabelsChange={handleShowLabelsChange}
                                sensorOptions={filteredSensorOptions}
                                selectedSensor={selectedSensor}
                                handleSensorChange={handleSensorChange}
                                companyOptions={companyOptions}
                                selectedCompany={isSuperOrPowerAdmin ? selectedCompany : null}
                                handleCompanyChange={isSuperOrPowerAdmin ? handleCompanyChange : null}
                                isSuperAdmin={isSuperOrPowerAdmin}
                                selectedGroup={selectedGroup}
                                handleGroupChange={handleGroupChange}
                                groupOptions={groupOptions}
                            />
                        </Paper>
                    </Grid>
                    <Grid item xs={12}>
                        <YearlyContent
                            selectedSensor={selectedSensor}
                            showLabels={showLabels}
                            processedData={processedData}
                            chartIsLoading={chartIsLoading}
                            error={error}
                            isSmallScreen={isSmallScreen}
                            sensorLabel={getSelectedSensorLabel()}
                            tabValue={tabValue}
                        />
                    </Grid>
                </Grid>
            ) : (
                // Web view
                <Grid container spacing={2} paddingBottom={3}>
                    <Grid item xs={12} md={10}>
                        <YearlyHeader isSmallScreen={isSmallScreen} />
                        <YearlyLabel sensorLabel={getSelectedSensorLabel()} isSmallScreen={isSmallScreen} tabValue={tabValue} onTabChange={handleTabChange} addonData={addonData} />
                        <YearlyContent
                            selectedSensor={selectedSensor}
                            showLabels={showLabels}
                            processedData={processedData}
                            chartIsLoading={chartIsLoading}
                            error={error}
                            isSmallScreen={isSmallScreen}
                            sensorLabel={getSelectedSensorLabel()}
                            tabValue={tabValue}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Paper
                            sx={{
                                position: 'sticky',
                                top: 20,
                                height: 'auto',
                                overflow: 'auto',
                                borderRadius: '15px',
                                boxShadow: '8px 8px 20px rgba(0, 0, 0, 0.1)',
                            }}
                        >
                            <YearlyFilterBox
                                sensorLabel={getSelectedSensorLabel()}
                                isSmallScreen={isSmallScreen}
                                showLabels={showLabels}
                                onShowLabelsChange={handleShowLabelsChange}
                                sensorOptions={filteredSensorOptions}
                                selectedSensor={selectedSensor}
                                handleSensorChange={handleSensorChange}
                                companyOptions={companyOptions}
                                selectedCompany={isSuperOrPowerAdmin ? selectedCompany : null}
                                handleCompanyChange={isSuperOrPowerAdmin ? handleCompanyChange : null}
                                isSuperAdmin={isSuperOrPowerAdmin}
                                selectedGroup={selectedGroup}
                                handleGroupChange={handleGroupChange}
                                groupOptions={groupOptions}
                            />
                        </Paper>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
};

export default Yearly;
